import type { DevToolConfig } from './types';

const DevToolsConfig = [
  {
    key: 'MAE_CONVERT_STEP',
    enabled: false,
    description: 'Shows the Convert step in the Integration Converters run flow. Off by default.',
  },
  {
    key: 'CODE_REMEDY_ANALYSIS',
    enabled: false,
    description:
      'Enables the "Generate progress report" button on the Code Remediation project page, which starts a remediation analysis job and offers the finished report for download (backend: POST /code-remediation-v3/remediation-analysis).',
  },
  {
    key: 'CODE_REMEDY_SUMMARY_REPORT',
    enabled: false,
    description:
      'Enables the "Generate summary" button and the remediation summary dashboard on the Code Remediation project page: finding-grained tracking report (Summary + Working list XLSX) and live counts (backend: /code-remediation-v3/remedies/{id}/dashboard and .../reports).',
  },
  {
    key: 'FOUNDRY_REPLICATION_SCHEDULE',
    enabled: false,
    description:
      'Shows on-demand and scheduled Foundry replication schedule controls. Enable only where backend schedule execution is enabled.',
  },
  {
    key: 'CHAT_MARKDOWN_COLORS',
    enabled: false,
    description:
      'Applies VSCode-style semantic color highlights to agent chat message text (headings, bold, lists, code, etc.).',
  },
  {
    key: 'SHOW_BUSINESS_DASHBOARD',
    enabled: false,
    description:
      'Replaces the Usage dashboard entry with the Business Insights dashboard. When off, users with usage roles keep the existing /usage experience; when on, the user menu and /business-dashboard route surface the new dashboard.',
  },
  {
    key: 'SHOW_LEGACY_INTEGRATIONS',
    enabled: true,
    description:
      'Shows integrations configured via legacy tenant settings and credential stores on the Connectors Systems page, marked as Legacy, with create/edit/remove of tenant-settings slots. Additionally gated on tenant-admin/tenant-manage roles.',
  },
  {
    key: 'VIEW_REMEDIATION_CHAT',
    enabled: false,
    description: 'Shows the remediation chat panel on the View Remediation detail page.',
  },
  {
    key: 'LOOM_EXECUTION_CARDS',
    enabled: false,
    description:
      'Shows run-result cards under each node on the loom execution canvas, with the wider row spacing that fits them.',
  },
  {
    key: 'WORKBENCH_PROJECT_TYPES',
    enabled: false,
    description:
      'Shows the project-type picker in the workbench Create Project modal — the only way to create a Code Modernization project. When off, creating a project looks and behaves exactly as it did before and every new project is GENERAL. Projects that already have a type keep it and still render their own tabs; this gates creation, not display.',
  },
] as const satisfies DevToolConfig<string>[];

export type DevToolKey = (typeof DevToolsConfig)[number]['key'];

function sortDevTools(configs: DevToolConfig<DevToolKey>[]): DevToolConfig<DevToolKey>[] {
  const sorted: DevToolConfig<DevToolKey>[] = [];
  const visited = new Set<DevToolKey>();
  const visiting = new Set<DevToolKey>(); // To detect circular dependencies

  const visit = (config: DevToolConfig<DevToolKey>) => {
    if (visited.has(config.key)) return;
    if (visiting.has(config.key)) {
      throw new Error(`Circular dependency detected at dev tool: ${config.key}`);
    }

    visiting.add(config.key);

    // Collect all dependencies from 'except' blocks
    const dependencies =
      config.except?.flatMap((ex) => {
        const deps: DevToolKey[] = [];
        if (ex.feature) deps.push(ex.feature);
        if (ex.features_required) deps.push(...ex.features_required);
        return deps;
      }) ?? [];

    for (const depKey of dependencies) {
      const depConfig = configs.find((c) => c.key === depKey);
      if (depConfig) {
        visit(depConfig);
      }
      // If depConfig isn't found, it's an external flag or a typo,
      // which evaluateDevToolConfig already logs.
    }

    visiting.delete(config.key);
    visited.add(config.key);
    sorted.push(config);
  };

  configs.forEach((config) => visit(config));
  return sorted;
}

export const DevTools = sortDevTools(DevToolsConfig); // satisfies DevToolConfig<DevToolKey>[];
